<template>
  <div class="cash-register-receive"
       :class="{'page-container': !embedded, 'cash-register-page': embedded}">
    <portal to="page-name" v-if="!embedded">Caixa</portal>
    <div :class="{'card card-page': !embedded}" v-if="hasAccess">
      <div class="card-header" v-if="!embedded">
        <h1 class="card-title">
          <router-link to="/cash-register" class="text-gray mr-1">
            <fa-icon :icon="['fal', 'arrow-left']"/>
          </router-link>
          Receber atendimento
        </h1>
      </div>

      <div :class="bodyClass" v-if="loading">
        <div class="loading loading-lg"></div>
      </div>

      <div :class="bodyClass" v-else-if="!session">
        <open-session v-if="embedded" @opened="onSessionOpened"/>
        <div class="empty" v-else>
          <div class="empty-icon"><fa-icon :icon="['fal', 'lock']" size="3x"/></div>
          <p class="empty-title h5">Abra o seu caixa antes de receber</p>
          <p class="empty-subtitle">
            <router-link to="/cash-register">Ir para o caixa</router-link>
          </p>
        </div>
      </div>

      <div :class="bodyClass" v-else-if="!appointment">
        <div class="empty">
          <div class="empty-icon"><fa-icon :icon="['fal', 'check-circle']" size="3x"/></div>
          <p class="empty-title h5">Este atendimento não tem valor particular pendente</p>
          <p class="empty-subtitle" v-if="!embedded">
            <router-link to="/cash-register">Voltar ao caixa</router-link>
          </p>
        </div>
      </div>

      <div :class="bodyClass" v-else-if="done">
        <div class="cr-box done">
          <div class="empty">
            <div class="empty-icon"><fa-icon :icon="['fal', 'check-circle']" size="2x"/></div>
            <p class="empty-title h5">{{ doneTitle }} · nº {{ done.code }}</p>
            <p class="empty-subtitle">
              {{ done.patient.name }}
              <template v-if="doneReturn"> · Retorno sem cobrança</template>
              <template v-else> · {{ done.totals.paid | currency }}</template>
              <template v-if="done.payer && done.payer.name !== done.patient.name">
                · pago por {{ done.payer.name }}
              </template>
            </p>
          </div>
        </div>
        <template v-if="!embedded">
          <button class="btn btn-primary btn-icon btn-icon-left mr-2"
                  @click="printReceipt"
                  :disabled="printing"
                  :class="{loading: printing}">
            <fa-icon :icon="['fal', 'print']"/>Recibo
          </button>
          <button class="btn btn-secondary btn-icon btn-icon-left mr-2" v-if="canIssueInvoice"
                  @click="openInvoice" :disabled="invoice.loading">
            <fa-icon :icon="['fal', 'file-invoice-dollar']"/>Emitir NFS-e
          </button>
          <router-link to="/cash-register" class="btn btn-gray btn-icon btn-icon-left">
            <fa-icon :icon="['fal', 'arrow-left']"/>Voltar ao caixa
          </router-link>
        </template>

        <invoice-detail-or-create
          v-if="invoice.show"
          :show="invoice.show"
          :total-amount="done.totals.paid"
          :entity="invoiceEntity"
          origin-type="cash_register_entry"
          :origin-id="done.id"
          @loading="invoice.loading = $event"
          @close="invoice.show = false"
        />
      </div>

      <div :class="bodyClass" v-else>
        <div class="columns">
          <div class="column col-4 col-md-12 form-group">
            <label class="form-label">{{ multiPatient ? 'Pacientes' : 'Paciente' }}</label>
            <input type="text" class="form-input text-bold" readonly :value="patientNames">
          </div>
          <div class="column col-4 col-md-12 form-group">
            <label class="form-label">{{ multiPatient ? 'Profissionais' : 'Profissional' }}</label>
            <input type="text" class="form-input text-bold" readonly :value="professionalNames">
          </div>
          <div class="column col-4 col-md-12 form-group">
            <label class="form-label">{{ multiPatient ? 'Atendimentos' : 'Atendimento' }}</label>
            <input type="text" class="form-input text-bold" readonly :value="appointmentDates">
          </div>
        </div>

        <div class="cr-section">
          <div class="cr-section-title">Itens do atendimento</div>
          <div class="cr-items">
            <table class="table">
              <thead>
              <tr>
                <th v-if="multiPatient">Paciente</th>
                <th>Item</th>
                <th>Tipo</th>
                <th class="text-center">Qtde</th>
                <th class="text-right">Unitário</th>
                <th class="text-right" style="width: 160px" v-if="canDiscount">Desconto</th>
                <th class="text-right">Total</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="item in items" :key="item.id">
                <td v-if="multiPatient">{{ item.patient.name }}</td>
                <td>{{ item.expense.name }}</td>
                <td>
                  <span class="text-error" v-if="item.return">Retorno</span>
                  <template v-else>{{ typeName(item.expense.type) }}</template>
                </td>
                <td class="text-center">{{ item.quantity }}</td>
                <td class="text-right">{{ item.unitValue | currency }}</td>
                <td class="text-right" v-if="canDiscount">
                  <div class="input-group cr-item-discount" v-if="item.unitValue > 0">
                    <span class="input-group-addon addon-sm text-gray">R$</span>
                    <dx-input-number v-model="item.discount" class="form-input input-sm text-right"
                                     :precision="2" maxlength="10" :disabled="locked"/>
                  </div>
                  <span class="text-gray" v-else>-</span>
                </td>
                <td class="text-right text-bold">{{ itemTotal(item) | currency }}</td>
              </tr>
              </tbody>
            </table>

            <div class="cr-totals">
              <div class="cr-totals-note">
                <template v-if="courtesy">
                  <fa-icon :icon="['fal', 'gift']" class="mr-1"/>
                  {{ courtesyLabel }}: nenhum valor será cobrado
                </template>
                <template v-else-if="locked">
                  <fa-icon :icon="['fal', 'lock']" class="mr-1"/>
                  Desconto e acréscimo travados após o primeiro pagamento
                </template>
              </div>
              <div class="cr-totals-list">
                <div class="cr-totals-row">
                  <span class="cr-totals-label">Total dos serviços</span>
                  <span class="cr-totals-value">{{ gross | currency }}</span>
                </div>
                <div class="cr-totals-row" v-if="canDiscount && itemDiscount > 0">
                  <span class="cr-totals-label">Descontos nos itens</span>
                  <span class="cr-totals-value text-error">- {{ itemDiscount | currency }}</span>
                </div>
                <div class="cr-totals-row" v-if="canDiscount">
                  <span class="cr-totals-label">
                    Desconto geral
                    <small v-if="discount.type === 'percent' && globalDiscount > 0">
                      - {{ globalDiscount | currency }}
                    </small>
                  </span>
                  <span class="cr-totals-field input-group">
                    <select class="form-select select-sm cr-unit"
                            v-model="discount.type"
                            :disabled="locked">
                      <option value="percent">%</option>
                      <option value="value">R$</option>
                    </select>
                    <dx-input-number v-if="discount.type === 'percent'"
                                     v-model="discount.percent"
                                     class="form-input input-sm text-right"
                                     :precision="2"
                                     maxlength="6"
                                     :disabled="locked"/>
                    <dx-input-number v-else
                                     v-model="discount.value"
                                     class="form-input input-sm text-right"
                                     :precision="2"
                                     maxlength="10"
                                     :disabled="locked"/>
                  </span>
                </div>
                <div class="cr-totals-row" v-if="canDiscount">
                  <span class="cr-totals-label">
                    Acréscimo
                    <small v-if="interest.type === 'percent' && globalInterest > 0">
                      + {{ globalInterest | currency }}
                    </small>
                  </span>
                  <span class="cr-totals-field input-group">
                    <select class="form-select select-sm cr-unit"
                            v-model="interest.type"
                            :disabled="locked">
                      <option value="percent">%</option>
                      <option value="value">R$</option>
                    </select>
                    <dx-input-number v-if="interest.type === 'percent'"
                                     v-model="interest.percent"
                                     class="form-input input-sm text-right"
                                     :precision="2"
                                     maxlength="6"
                                     :disabled="locked"/>
                    <dx-input-number v-else
                                     v-model="interest.value"
                                     class="form-input input-sm text-right"
                                     :precision="2"
                                     maxlength="10"
                                     :disabled="locked"/>
                  </span>
                </div>
                <div class="cr-totals-row cr-totals-net" v-if="net !== gross">
                  <span class="cr-totals-label">Valor do atendimento</span>
                  <span class="cr-totals-value">{{ net | currency }}</span>
                </div>
                <div class="cr-totals-row" v-if="rateTotal > 0">
                  <span class="cr-totals-label">
                    Taxas das operadoras
                    <small v-if="payerRateTotal > 0">
                      {{ payerRateTotal | currency }} cobradas do paciente
                    </small>
                  </span>
                  <span class="cr-totals-value">{{ rateTotal | currency }}</span>
                </div>
                <div class="cr-totals-row" v-if="payments.length > 0">
                  <span class="cr-totals-label">
                    Pagamentos informados
                    <small v-if="charged !== paid">cobrado {{ charged | currency }}</small>
                  </span>
                  <span class="cr-totals-value">- {{ paid | currency }}</span>
                </div>
                <div class="cr-totals-row" v-if="courtesy">
                  <span class="cr-totals-label">{{ courtesyLabel }}</span>
                  <span class="cr-totals-value text-error">- {{ net | currency }}</span>
                </div>
                <div class="cr-totals-row cr-totals-remaining" :class="{done: canFinish}">
                  <span class="cr-totals-label">Restante a pagar</span>
                  <span class="cr-totals-value" :class="{'text-error': remaining < 0}">
                    {{ remaining | currency }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="cr-section">
          <div class="cr-section-title">Pagamento</div>

          <table class="table table-hover cr-payments" v-if="payments.length > 0">
            <thead>
            <tr>
              <th>Forma</th>
              <th class="text-center">Parcelas</th>
              <th class="text-right">Valor</th>
              <th class="text-right">Taxa</th>
              <th class="text-right">Cobrado</th>
              <th style="width: 40px"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(payment, i) in payments" :key="i">
              <td>
                <strong>{{ payment.name }}</strong>
                <small class="text-gray"
                       v-if="payment.holderName && payment.holderId !== appointment.patient.id">
                  · saldo de {{ payment.holderName }}
                </small>
              </td>
              <td class="text-center">
                {{ payment.installments > 1 ? `${payment.installments}x` : 'à vista' }}
              </td>
              <td class="text-right">{{ payment.value | currency }}</td>
              <td class="text-right">
                <template v-if="payment.rate > 0">
                  {{ payment.rate | currency }}
                  <small class="text-gray">
                    {{ payment.rateBy === 'payer' ? 'paciente' : 'clínica' }}
                  </small>
                </template>
                <template v-else>-</template>
              </td>
              <td class="text-right text-bold">{{ payment.charged | currency }}</td>
              <td class="text-right">
                <button class="btn btn-gray btn-action btn-icon btn-sm"
                        @click="removePayment(i)">
                  <fa-icon :icon="['fal', 'times']"/>
                </button>
              </td>
            </tr>
            </tbody>
          </table>

          <div class="cr-box cr-payment-box" v-if="!courtesy && !isFullyPaid"
               :class="{'cr-payment-blocked': remaining <= 0}">
            <div class="cr-payment-head">
              <div class="cr-box-title">{{ paymentTitle }}</div>
              <div class="cr-payment-remaining">
                <template v-if="remaining < 0">
                  <fa-icon :icon="['fal', 'exclamation-triangle']" class="mr-1"/>
                  Desconto maior que o total. Ajuste para continuar.
                </template>
                <template v-else-if="remaining === 0">
                  <fa-icon :icon="['fal', 'exclamation-triangle']" class="mr-1"/>
                  Nada a receber. Registre como cortesia ou ajuste o desconto.
                </template>
                <template v-else>
                  Restante <strong>{{ remaining | currency }}</strong>
                </template>
              </div>
            </div>
            <div class="cr-payment-grid">
              <button class="cr-credit-card" @click="credit.show = true"
                      :disabled="!creditMethod || remaining <= 0">
                <span class="cr-credit-icon">
                  <fa-icon :icon="['fal', 'hand-holding-usd']"/>
                </span>
                <span class="cr-credit-body">
                  <span class="cr-credit-title">Paciente com saldo em conta</span>
                  <span class="cr-credit-balance" v-if="credit.loading">
                    <span class="loading"></span>
                  </span>
                  <span class="cr-credit-balance"
                        :class="{'is-empty': patientCreditAvailable <= 0}"
                        v-else>{{ patientCreditAvailable | currency }}</span>
                  <span class="cr-credit-hint" v-if="patientCreditAvailable > 0">
                    Clique para usar o saldo do paciente
                  </span>
                  <span class="cr-credit-hint" v-else>
                    Sem saldo. Dá para usar o de um responsável
                  </span>
                </span>
                <span class="cr-credit-action">
                  Usar <fa-icon :icon="['fal', 'chevron-right']"/>
                </span>
              </button>
              <div class="cr-payment-form">
                <div class="cr-payment-form-title">Forma de pagamento</div>
                <div class="cr-fields">
                  <div class="form-group cr-field-method"
                       :class="{'has-error': $v.paymentForm.methodId.$error}">
                    <label class="form-label">Forma</label>
                    <select class="form-select"
                            v-model="paymentForm.methodId"
                            :disabled="remaining <= 0"
                            @change="onMethodChange"
                            @blur="$v.paymentForm.methodId.$touch()">
                      <option value="">[Selecione]</option>
                      <option v-for="item in methods"
                              :value="item.id"
                              :key="item.id">{{ item.name }}</option>
                    </select>
                    <div class="form-input-hint" v-if="$v.paymentForm.methodId.$error">
                      Campo obrigatório
                    </div>
                  </div>
                  <div class="form-group cr-field-installments"
                       v-if="installmentOptions.length > 1">
                    <label class="form-label">Parcelas</label>
                    <select class="form-select" v-model="paymentForm.installments"
                            :disabled="remaining <= 0">
                      <option v-for="n in installmentOptions"
                              :value="n"
                              :key="n">{{ n }}x</option>
                    </select>
                  </div>
                  <div class="form-group cr-field-value"
                       :class="{'has-error': $v.paymentForm.value.$error}">
                    <label class="form-label">
                      {{ selectedRateByPayer ? 'Valor a cobrar' : 'Valor' }}
                    </label>
                    <dx-input-number id="payment-value"
                                     v-model="paymentForm.value"
                                     class="form-input text-right"
                                     :precision="2"
                                     maxlength="10"
                                     :disabled="remaining <= 0"
                                     @focus="fillRemaining()"
                                     @blur="$v.paymentForm.value.$touch()"/>
                    <template v-if="$v.paymentForm.value.$error">
                      <div class="form-input-hint" v-if="!$v.paymentForm.value.minValue">
                        Informe o valor
                      </div>
                      <div class="form-input-hint" v-else-if="!$v.paymentForm.value.maxValue">
                        Maior que o restante a pagar
                      </div>
                    </template>
                  </div>
                  <div class="form-group cr-field-action">
                    <label class="form-label">&nbsp;</label>
                    <button class="btn btn-primary btn-icon btn-icon-left" @click="addPayment"
                            :disabled="remaining <= 0">
                      <fa-icon :icon="['fal', 'plus']"/>Adicionar
                    </button>
                  </div>
                </div>
                <div class="cr-fee" v-if="selectedRatePercent > 0"
                     :class="selectedRateByPayer ? 'cr-fee-payer' : 'cr-fee-merchant'">
                  <span class="cr-fee-badge">
                    <fa-icon :icon="['fal', selectedRateByPayer ? 'user' : 'clinic-medical']"/>
                    {{ selectedRateByPayer ? 'Taxa paga pelo paciente' : 'Taxa paga pela clínica' }}
                  </span>
                  <span class="cr-fee-detail" v-if="selectedRateByPayer">
                    Valor do atendimento <strong>{{ formValue | currency }}</strong>
                  </span>
                  <span class="cr-fee-detail">
                    Taxa {{ selectedRateLabel }} <strong>{{ formRate | currency }}</strong>
                  </span>
                  <span class="cr-fee-detail" v-if="!selectedRateByPayer">
                    Líquido para a clínica <strong>{{ formNet | currency }}</strong>
                  </span>
                </div>
              </div>
            </div>
            <div class="cr-courtesy" v-if="canDiscount && payments.length === 0">
              <fa-icon :icon="['fal', 'gift']" class="mr-1"/>
              Não vai cobrar este atendimento?
              <a href="#" @click.prevent="courtesy = true">Registrar como cortesia</a>
            </div>
          </div>

          <div class="cr-box cr-payment-box cr-courtesy-box" v-else-if="courtesy">
            <div class="cr-courtesy-head">
              <span class="cr-courtesy-icon"><fa-icon :icon="['fal', 'gift']"/></span>
              <div>
                <div class="cr-courtesy-title">{{ courtesyLabel }} · nenhum valor será cobrado</div>
                <div class="cr-courtesy-text" v-if="allReturn">
                  Consulta de retorno, sem cobrança. O atendimento será registrado como retorno
                  e liberado na agenda. Nada entra no caixa nem no financeiro.
                </div>
                <div class="cr-courtesy-text" v-else>
                  O atendimento de {{ net | currency }} será registrado como gratuito e liberado
                  na agenda. Nada entra no caixa nem no financeiro.
                </div>
              </div>
              <button class="btn btn-gray btn-icon btn-icon-left" @click="courtesy = false"
                      v-if="!allReturn">
                <fa-icon :icon="['fal', 'undo']"/>Cobrar mesmo assim
              </button>
            </div>
          </div>

          <div class="cr-box done" v-else-if="isFullyPaid">
            <div class="empty">
              <div class="empty-icon"><fa-icon :icon="['fal', 'check-circle']" size="2x"/></div>
              <p class="empty-subtitle">
                Formas de pagamento informadas. Confira o nome do recibo e clique em
                <strong>Finalizar</strong>.
              </p>
            </div>
          </div>
        </div>

        <div class="cr-section">
          <div class="cr-payer" v-if="!courtesy">
            <div class="cr-payer-toggle" @click="showPayer = !showPayer">
              <fa-icon :icon="['fal', showPayer ? 'chevron-up' : 'chevron-down']" class="mr-1"/>
              Recibo em nome de <strong>{{ payerLabel }}</strong>
              <span class="text-primary ml-1">{{ showPayer ? 'ocultar' : 'alterar' }}</span>
            </div>
            <div class="columns mt-2" v-if="showPayer">
              <div class="column col-12 form-group">
                <label class="form-radio form-inline" v-if="!multiPatient">
                  <input type="radio" :value="true" v-model="payer.sameAsPatient">
                  <i class="form-icon"></i> O próprio paciente
                </label>
                <label class="form-radio form-inline">
                  <input type="radio" :value="false" v-model="payer.sameAsPatient">
                  <i class="form-icon"></i> Outra pessoa ou empresa
                </label>
              </div>
              <template v-if="!payer.sameAsPatient">
                <div class="column col-4 col-sm-12 form-group">
                  <label class="form-label">Documento</label>
                  <st-input-identity v-model="payer.identity" :types="identityTypes"/>
                </div>
                <div class="column col-8 col-sm-12 form-group"
                     :class="{'has-error': $v.payer.name.$error}">
                  <label class="form-label">Nome</label>
                  <input type="text" class="form-input" v-model="payer.name"
                         @blur="$v.payer.name.$touch()">
                  <div class="form-input-hint" v-if="$v.payer.name.$error">
                    Campo obrigatório
                  </div>
                </div>
              </template>
            </div>
          </div>

          <div class="form-group mt-2">
            <label class="form-label">Observação</label>
            <input type="text" class="form-input" v-model="notes">
          </div>

          <div class="cr-actions" v-if="!embedded">
            <button class="btn btn-primary btn-lg btn-icon btn-icon-left"
                    :class="{loading: saving}"
                    :disabled="saving || !canFinish"
                    @click="conclude">
              <fa-icon :icon="['fal', 'check']"/>
              {{ courtesy ? `Finalizar ${courtesyLabel.toLowerCase()}` : 'Finalizar recebimento' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <forbidden v-else></forbidden>

    <use-credit-modal
      v-if="credit.show"
      :show="credit.show"
      :patient="appointment ? appointment.patient : null"
      :max-value="remaining"
      :already-used="creditUsedByHolder"
      @confirm="addCreditPayment"
      @close="credit.show = false"
    />
  </div>
</template>

<script>
import { round } from 'lodash';
import { mapState } from 'vuex';
import {
  required,
  requiredIf,
  minValue,
} from 'vuelidate/lib/validators';
import { CPF, CNPJ } from '@/data/identity-types';
import {
  LIST_CASHIER_ATTENDANCES,
  ALLOW_CASHIER_DISCOUNT_INTEREST,
  CREATE_FISCAL_DOCUMENT,
} from '@/data/actions/modules/financial';
import { getName as typeName } from '@/data/expense-types';
import Forbidden from '@/components/auth/Forbidden.vue';
import InvoiceDetailOrCreate from '@/components/fiscal-document/FiscalDocument.vue';
import UseCreditModal from '../modals/UseCredit.vue';
import OpenSession from './OpenSession.vue';

const INSTALLMENT_TYPES = ['billet', 'credit_card', 'check', 'duplicate', 'others'];

export default {
  components: {
    Forbidden,
    InvoiceDetailOrCreate,
    UseCreditModal,
    OpenSession,
  },
  props: {
    appointmentIds: {
      type: Array,
      default: null,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      saving: false,
      printing: false,
      identityTypes: [CPF, CNPJ],
      session: null,
      appointments: [],
      items: [],
      methods: [],
      creditMethod: null,
      discount: { type: 'value', percent: 0, value: 0 },
      interest: { type: 'value', percent: 0, value: 0 },
      payments: [],
      courtesy: false,
      paymentForm: { methodId: '', installments: 1, value: 0 },
      autoValue: 0,
      payer: {
        sameAsPatient: true,
        name: '',
        identity: { type: CPF, value: '' },
      },
      showPayer: false,
      notes: '',
      credit: { show: false, loading: false, balance: 0 },
      done: null,
      invoice: { show: false, loading: false },
    };
  },
  computed: {
    ...mapState({
      branch: ({ auth }) => auth.user.branch,
    }),
    hasAccess() {
      return this.$can(LIST_CASHIER_ATTENDANCES);
    },
    bodyClass() {
      return this.embedded ? 'cr-embedded-body' : 'card-body';
    },
    footerState() {
      return {
        editing: !this.loading && !!this.session && !!this.appointment && !this.done,
        canFinish: this.canFinish,
        saving: this.saving,
        label: this.courtesy
          ? `Finalizar ${this.courtesyLabel.toLowerCase()}`
          : 'Finalizar recebimento',
        done: !!this.done,
        printing: this.printing,
        canIssueInvoice: !!this.done && this.canIssueInvoice,
        invoiceLoading: this.invoice.loading,
      };
    },
    appointment() {
      return this.appointments.length > 0 ? this.appointments[0] : null;
    },
    patients() {
      return this.appointments
        .map(({ patient }) => patient)
        .filter((patient, index, list) => patient
          && list.findIndex(other => other && other.id === patient.id) === index);
    },
    multiPatient() {
      return this.patients.length > 1;
    },
    patientNames() {
      return this.patients.map(({ name }) => name).join(', ');
    },
    professionalNames() {
      const names = this.appointments
        .map(({ professional }) => (professional ? professional.name : null))
        .filter((name, index, list) => name && list.indexOf(name) === index);
      return names.length > 0 ? names.join(', ') : '-';
    },
    appointmentDates() {
      return this.appointments
        .map(({ startDate }) => this.$options.filters.date(startDate, 'datetime'))
        .join(', ');
    },
    canDiscount() {
      return this.$can(ALLOW_CASHIER_DISCOUNT_INTEREST);
    },
    canIssueInvoice() {
      const settings = this.branch.settings.fiscalDocument;
      return settings && settings.enabled
        && this.$can(CREATE_FISCAL_DOCUMENT)
        && !this.done.totals.courtesy;
    },
    locked() {
      return this.payments.length > 0 || this.courtesy;
    },
    gross() {
      return round(this.items.reduce((a, b) => a + b.unitValue * b.quantity, 0), 2);
    },
    itemDiscount() {
      return round(this.items.reduce((a, b) => a + (Number(b.discount) || 0), 0), 2);
    },
    globalDiscount() {
      if (this.discount.type === 'percent') {
        return round((this.gross * (Number(this.discount.percent) || 0)) / 100, 2);
      }
      return round(Number(this.discount.value) || 0, 2);
    },
    globalInterest() {
      if (this.interest.type === 'percent') {
        return round((this.gross * (Number(this.interest.percent) || 0)) / 100, 2);
      }
      return round(Number(this.interest.value) || 0, 2);
    },
    net() {
      return round(this.gross - this.itemDiscount - this.globalDiscount + this.globalInterest, 2);
    },
    paid() {
      return round(this.payments.reduce((a, b) => a + b.value, 0), 2);
    },
    charged() {
      return round(this.payments.reduce((a, b) => a + b.charged, 0), 2);
    },
    payerRateTotal() {
      return round(this.payments
        .filter(({ rateBy }) => rateBy === 'payer')
        .reduce((a, b) => a + (b.rate || 0), 0), 2);
    },
    remaining() {
      if (this.courtesy) return 0;
      return round(this.net - this.paid, 2);
    },
    isFullyPaid() {
      return this.payments.length > 0 && this.remaining <= 0;
    },
    canFinish() {
      return this.courtesy || this.isFullyPaid;
    },
    allReturn() {
      return this.items.length > 0 && this.items.every(({ return: isReturn }) => isReturn);
    },
    courtesyLabel() {
      return this.allReturn ? 'Retorno' : 'Cortesia';
    },
    doneReturn() {
      if (!this.done || !this.done.totals.courtesy) return false;
      const items = this.done.items || [];
      return items.length > 0 && items.every(item => item.return);
    },
    doneTitle() {
      if (this.doneReturn) return 'Retorno registrado';
      return this.done.totals.courtesy ? 'Cortesia registrada' : 'Recebimento concluído';
    },
    rateTotal() {
      return round(this.payments.reduce((a, b) => a + (b.rate || 0), 0), 2);
    },
    selectedMethod() {
      return this.methods.find(({ id }) => id === this.paymentForm.methodId) || null;
    },
    installmentOptions() {
      const method = this.selectedMethod;
      if (!method || !INSTALLMENT_TYPES.includes(method.documentType.type)) return [1];
      const max = (method.installments || []).reduce((a, b) => Math.max(a, b.installment), 1);
      return Array.from({ length: max }, (_, i) => i + 1);
    },
    selectedRateSetting() {
      const method = this.selectedMethod;
      if (!method) return null;
      return (method.installments || [])
        .find(i => i.installment === this.paymentForm.installments) || null;
    },
    selectedRatePercent() {
      return this.selectedRateSetting ? Number(this.selectedRateSetting.value) || 0 : 0;
    },
    selectedRateLabel() {
      return `${String(this.selectedRatePercent).replace('.', ',')}%`;
    },
    selectedRateByPayer() {
      return this.selectedRatePercent > 0 && this.selectedRateSetting.interestByMerchant === false;
    },
    formTyped() {
      return round(Number(this.paymentForm.value) || 0, 2);
    },
    formValue() {
      if (!this.selectedRateByPayer) return this.formTyped;
      return round(this.formTyped / (1 + this.selectedRatePercent / 100), 2);
    },
    formRate() {
      if (this.selectedRateByPayer) return round(this.formTyped - this.formValue, 2);
      return round((this.formTyped * this.selectedRatePercent) / 100, 2);
    },
    formCharged() {
      return this.selectedRateByPayer ? this.formTyped : this.formValue;
    },
    formNet() {
      return round(this.formValue - this.formRate, 2);
    },
    suggestedValue() {
      const remaining = Math.max(this.remaining, 0);
      if (!this.selectedRateByPayer) return round(remaining, 2);
      return round(remaining * (1 + this.selectedRatePercent / 100), 2);
    },
    creditUsedByHolder() {
      return this.payments
        .filter(({ type }) => type === 'credit')
        .reduce((acc, p) => ({ ...acc, [p.holderId]: (acc[p.holderId] || 0) + p.value }), {});
    },
    patientCreditAvailable() {
      const used = this.appointment ? this.creditUsedByHolder[this.appointment.patient.id] || 0 : 0;
      return round(Math.max(this.credit.balance - used, 0), 2);
    },
    paymentTitle() {
      if (this.payments.length === 0) return 'Como o paciente vai pagar?';
      return `Como pagar o restante de ${this.formatCurrency(this.remaining)}?`;
    },
    payerLabel() {
      if (this.payer.sameAsPatient || !this.payer.name) {
        return this.appointment ? this.appointment.patient.name : '';
      }
      return this.payer.name;
    },
    invoiceEntity() {
      if (!this.done) return null;
      const { payer, patient } = this.done;
      const id = payer && payer.entityId ? payer.entityId : patient.id;
      return { id, name: this.done.payer ? this.done.payer.name : this.done.patient.name };
    },
  },
  watch: {
    footerState: {
      immediate: true,
      handler(state) {
        this.$emit('state', state);
      },
    },
    suggestedValue() {
      this.fillRemaining();
    },
  },
  validations() {
    return {
      paymentForm: {
        methodId: { required },
        value: {
          required,
          minValue: minValue(0.01),
          maxValue: () => this.formValue <= round(Math.max(this.remaining, 0), 2),
        },
      },
      payer: {
        name: { required: requiredIf(() => !this.payer.sameAsPatient) },
      },
    };
  },
  async mounted() {
    if (!this.hasAccess) {
      this.loading = false;
      return;
    }
    try {
      const { data: session } = await this.$http.get('/cash-register/session');
      this.session = session.status === 'open' ? session : null;
      if (this.session) {
        await this.loadAll();
      }
    } catch (e) {
      this.$toast.error(e);
    }
    this.loading = false;
  },
  methods: {
    typeName,
    formatCurrency(value) {
      return this.$options.filters.currency(value);
    },
    loadAll() {
      return Promise.all([this.loadAppointment(), this.loadMethods(), this.loadCreditMethod()])
        .then(() => this.loadCreditBalance());
    },
    async onSessionOpened(session) {
      this.session = session;
      this.loading = true;
      try {
        await this.loadAll();
      } catch (e) {
        this.$toast.error(e);
      }
      this.loading = false;
    },
    routeAppointmentIds() {
      return this.$route.params.appointmentId
        ? [this.$route.params.appointmentId]
        : String(this.$route.query.ids || '').split(',').filter(Boolean);
    },
    async loadAppointment() {
      const ids = this.appointmentIds && this.appointmentIds.length > 0
        ? this.appointmentIds
        : this.routeAppointmentIds();
      if (ids.length === 0) {
        this.appointments = [];
        return;
      }
      const params = { appointmentIds: ids.join(',') };
      const { data } = await this.$http.get('/cash-register/pending-appointments', { params });
      this.appointments = data.items;
      this.items = data.items.flatMap(appointment => appointment.items.map(item => ({
        ...item,
        appointmentId: appointment.id,
        patient: appointment.patient,
        unitValue: round(item.total / (item.quantity || 1), 2),
        discount: 0,
      })));
      if (this.multiPatient) {
        this.payer.sameAsPatient = false;
        this.showPayer = true;
      }
      this.courtesy = this.canDiscount && this.allReturn;
    },
    loadMethods() {
      const params = { active: true, limit: 0 };
      return this.$http.get('/payment-methods', { params })
        .then(({ data }) => {
          this.methods = data.items.filter(({ type }) => ['all', 'revenue'].includes(type));
        });
    },
    loadCreditMethod() {
      return this.$http.get('/patient-credits/usage-method')
        .then(({ data }) => {
          this.creditMethod = data;
        })
        .catch(() => {
          this.creditMethod = null;
        });
    },
    loadCreditBalance() {
      if (!this.appointment) return;
      this.credit.loading = true;
      const params = { patientId: this.appointment.patient.id };
      this.$http.get('/patient-credits/balance', { params })
        .then(({ data }) => {
          this.credit.balance = data.balance || 0;
        })
        .catch(() => {
          this.credit.balance = 0;
        })
        .finally(() => {
          this.credit.loading = false;
        });
    },
    itemTotal(item) {
      return round(item.unitValue * item.quantity - (Number(item.discount) || 0), 2);
    },
    onMethodChange() {
      this.paymentForm.installments = 1;
      this.fillRemaining();
    },
    fillRemaining(force = false) {
      const current = round(Number(this.paymentForm.value) || 0, 2);
      const untouched = !(current > 0) || current === this.autoValue;
      if (force || untouched) {
        this.paymentForm.value = this.suggestedValue;
        this.autoValue = this.suggestedValue;
      }
    },
    addPayment() {
      this.$v.paymentForm.$touch();
      if (this.$v.paymentForm.$error) return;
      const method = this.selectedMethod;
      const value = this.formValue;
      this.payments.push({
        methodId: method.id,
        name: method.name,
        type: method.documentType.type,
        installments: this.paymentForm.installments,
        value,
        rate: this.formRate,
        rateBy: this.selectedRateByPayer ? 'payer' : 'merchant',
        charged: this.formCharged,
      });
      this.paymentForm = { methodId: '', installments: 1, value: 0 };
      this.autoValue = 0;
      this.$v.paymentForm.$reset();
      this.$nextTick(() => this.fillRemaining(true));
    },
    addCreditPayment({ holderId, holderName, value }) {
      this.payments.push({
        methodId: this.creditMethod.id,
        name: 'Saldo em conta',
        type: 'credit',
        installments: 1,
        value,
        rate: 0,
        rateBy: 'merchant',
        charged: value,
        holderId,
        holderName,
      });
    },
    removePayment(index) {
      this.payments.splice(index, 1);
    },
    conclude() {
      const { courtesy } = this;
      if (!courtesy && !this.isFullyPaid) return;
      if (courtesy && this.payments.length > 0) return;
      if (!courtesy) {
        this.$v.payer.$touch();
        if (this.$v.payer.$error) {
          this.showPayer = true;
          return;
        }
      }

      const data = {
        appointmentIds: this.appointments.map(({ id }) => id),
        items: this.items.map(item => ({
          appointmentExpenseId: item.id,
          quantity: item.quantity,
          discount: Number(item.discount) || 0,
        })),
        discount: this.globalDiscount,
        interest: this.globalInterest,
        courtesy,
        payments: courtesy ? [] : this.payments.map(payment => ({
          methodId: payment.methodId,
          installments: payment.installments,
          value: payment.value,
          holderId: payment.holderId || null,
        })),
        notes: this.notes || null,
      };

      if (!courtesy && !this.payer.sameAsPatient && this.payer.name) {
        data.payer = {
          name: this.payer.name,
          identity: this.payer.identity && this.payer.identity.value
            ? { type: this.payer.identity.type, value: this.payer.identity.value }
            : null,
        };
      }

      this.saving = true;
      this.$http.post('/cash-register/entries/receipt', data)
        .then(({ data: entry }) => {
          this.done = entry;
          this.$toast.success(`${this.doneTitle}.`);
          this.$emit('done', entry);
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    openInvoice() {
      this.invoice.show = true;
    },
    printReceipt() {
      this.printing = true;
      this.$file.print(`/cash-register/entries/${this.done.id}/receipt`)
        .catch(() => {})
        .finally(() => {
          this.printing = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
.cash-register-receive {
  .cr-items {
    border: $border-color-dark solid $border-width;
    border-radius: $border-radius;
    overflow: hidden;
    .table {
      margin-bottom: 0;
      thead th {
        background-color: $gray-color-ultra-light;
        border-bottom: $border-color solid $border-width;
      }
      th, td {
        padding-left: $layout-spacing;
        padding-right: $layout-spacing;
      }
      tbody tr:last-child td {
        border-bottom: 0;
      }
      .cr-item-discount {
        justify-content: flex-end;
        .form-input {
          flex: 0 0 5.5rem;
        }
      }
    }
  }
  .cr-totals {
    background-color: $gray-color-ultra-light;
    border-top: $border-color-dark solid $border-width;
    display: flex;
    gap: $layout-spacing-lg;
    justify-content: space-between;
    padding: $layout-spacing $layout-spacing-lg;
    .cr-totals-note {
      align-self: flex-end;
      color: $gray-color-dark;
      font-size: $font-size-sm;
    }
    .cr-totals-list {
      flex: 0 0 24rem;
      max-width: 100%;
    }
    .cr-totals-row {
      align-items: center;
      display: flex;
      gap: $layout-spacing;
      justify-content: space-between;
      padding: .2rem 0;
      .cr-totals-label {
        color: $gray-color-dark;
        small {
          display: block;
          font-size: $font-size-xs;
        }
      }
      .cr-totals-value {
        font-variant-numeric: tabular-nums;
        font-weight: 600;
      }
      .cr-totals-field {
        flex: 0 0 10rem;
        .cr-unit {
          flex: 0 0 3.2rem;
        }
      }
    }
    .cr-totals-net {
      border-top: $border-color solid $border-width;
      margin-top: .2rem;
      padding-top: .4rem;
    }
    .cr-totals-remaining {
      border-top: $border-color-dark solid $border-width;
      margin-top: .3rem;
      padding-top: .5rem;
      .cr-totals-label {
        color: $dark-color;
        font-weight: 600;
      }
      .cr-totals-value {
        color: $primary-color;
        font-size: 1.2rem;
        font-weight: 700;
      }
      &.done .cr-totals-value {
        color: darken($success-color, 10%);
      }
    }
    @media screen and (max-width: $size-sm) {
      flex-direction: column;
      .cr-totals-list {
        flex: 1 1 auto;
      }
    }
  }
  .cr-payments {
    margin-bottom: $layout-spacing;
  }
  .cr-payment-blocked {
    .cr-payment-remaining {
      color: darken($warning-color, 22%);
      font-weight: 600;
    }
  }
  .cr-payment-box {
    padding: $layout-spacing-lg;
    .cr-payment-head {
      align-items: baseline;
      display: flex;
      justify-content: space-between;
      margin-bottom: $layout-spacing;
      .cr-box-title {
        margin-bottom: 0;
      }
      .cr-payment-remaining {
        color: $gray-color-dark;
        font-size: $font-size-sm;
        strong {
          color: $primary-color;
          font-size: $font-size-lg;
          margin-left: $layout-spacing-sm;
        }
      }
    }
    &.cr-courtesy-box {
      background-color: lighten($warning-color, 40%);
      border-color: $warning-color;
      .cr-courtesy-head {
        align-items: center;
        display: flex;
        gap: $layout-spacing;
      }
      .cr-courtesy-icon {
        align-items: center;
        background-color: $warning-color;
        border-radius: 50%;
        color: $light-color;
        display: flex;
        flex: 0 0 auto;
        height: 2.2rem;
        justify-content: center;
        width: 2.2rem;
      }
      .cr-courtesy-title {
        color: darken($warning-color, 25%);
        font-weight: 600;
      }
      .cr-courtesy-text {
        color: $gray-color-dark;
        font-size: $font-size-sm;
      }
      .btn {
        margin-left: auto;
      }
    }
    .cr-courtesy {
      border-top: $border-color solid $border-width;
      color: $gray-color-dark;
      font-size: $font-size-sm;
      margin-top: $layout-spacing;
      padding-top: $layout-spacing-sm;
      a {
        font-weight: 600;
        margin-left: .2rem;
      }
    }
    .cr-payment-grid {
      display: flex;
      gap: $layout-spacing-lg;
      align-items: stretch;
      @media screen and (max-width: $size-md) {
        flex-direction: column;
      }
    }
    .cr-credit-card {
      align-items: center;
      background-color: $light-color;
      border: $border-color solid $border-width;
      border-radius: $border-radius;
      cursor: pointer;
      display: flex;
      flex: 0 0 19rem;
      gap: $layout-spacing;
      padding: $layout-spacing;
      text-align: left;
      .cr-credit-icon {
        align-items: center;
        background-color: lighten($primary-color, 42%);
        border-radius: 50%;
        color: $primary-color;
        display: flex;
        flex: 0 0 auto;
        font-size: 1rem;
        height: 2.2rem;
        justify-content: center;
        width: 2.2rem;
      }
      .cr-credit-body {
        display: flex;
        flex-direction: column;
        line-height: 1.2;
        min-width: 0;
      }
      .cr-credit-title {
        color: $primary-color;
        font-weight: 600;
      }
      .cr-credit-balance {
        color: $dark-color;
        font-size: 1.2rem;
        font-weight: 700;
        margin: .15rem 0;
        &.is-empty {
          color: $gray-color;
        }
        .loading {
          display: inline-block;
          height: 1rem;
          width: 1rem;
        }
      }
      .cr-credit-hint {
        color: $gray-color-dark;
        font-size: $font-size-xs;
      }
      .cr-credit-action {
        align-items: center;
        align-self: stretch;
        border-left: $border-color solid $border-width;
        color: $primary-color;
        display: flex;
        flex: 0 0 auto;
        font-size: $font-size-sm;
        font-weight: 600;
        gap: .2rem;
        margin-left: auto;
        padding-left: $layout-spacing;
      }
      &:hover:not(:disabled) {
        border-color: $primary-color;
        .cr-credit-action {
          border-left-color: $primary-color;
        }
      }
      &:disabled {
        border-color: $border-color;
        cursor: not-allowed;
        .cr-credit-icon {
          background-color: $gray-color-ultra-light;
          color: $gray-color;
        }
        .cr-credit-title, .cr-credit-action {
          color: $gray-color;
        }
      }
    }
    .cr-payment-form {
      background-color: $light-color;
      border: $border-color solid $border-width;
      border-radius: $border-radius;
      flex: 1 1 auto;
      min-width: 0;
      padding: $layout-spacing;
      .cr-payment-form-title {
        color: $gray-color-dark;
        font-size: $font-size-sm;
        font-weight: 600;
        margin-bottom: $layout-spacing-sm;
      }
    }
    .cr-fields {
      align-items: flex-start;
      display: flex;
      gap: 0 $layout-spacing;
      .form-group {
        margin-bottom: 0;
      }
      .cr-field-method {
        flex: 1 1 8rem;
        min-width: 0;
      }
      .cr-field-installments {
        flex: 0 0 4.5rem;
      }
      .cr-field-value {
        flex: 0 0 7rem;
      }
      .cr-field-action {
        flex: 0 0 auto;
      }
    }
    .cr-fee {
      align-items: center;
      border-radius: $border-radius;
      display: flex;
      flex-wrap: wrap;
      font-size: $font-size-sm;
      gap: .3rem $layout-spacing;
      margin-top: $layout-spacing-sm;
      padding: .35rem .6rem;
      .cr-fee-badge {
        font-weight: 600;
        svg {
          margin-right: .25rem;
        }
      }
      .cr-fee-detail {
        color: $gray-color-dark;
        strong {
          color: $dark-color;
          font-variant-numeric: tabular-nums;
          margin-left: .2rem;
        }
      }
      &.cr-fee-payer {
        background-color: #FFF8E6;
        border: #F2C94C solid $border-width;
        .cr-fee-badge {
          color: darken($warning-color, 22%);
        }
      }
      &.cr-fee-merchant {
        background-color: lighten($info-color, 48%);
        border: lighten($info-color, 34%) solid $border-width;
        .cr-fee-badge {
          color: darken($info-color, 10%);
        }
      }
    }
  }
  .cr-actions {
    display: flex;
    gap: $layout-spacing;
    margin-top: $layout-spacing;
  }
  .cr-payer {
    .cr-payer-toggle {
      color: $gray-color-dark;
      cursor: pointer;
      font-size: $font-size-sm;
      strong {
        color: $dark-color;
      }
    }
  }
}
</style>
